import * as THREE from 'three'
import gsap from 'gsap'
import Record from '../objects/Record'
import Background from '../objects/Background'

/**
 * Persistent scene holding every record. The camera is set up so that
 * 1 world unit = 1 CSS pixel at z = 0, with the origin at the viewport centre,
 * which lets DOM rects position the meshes directly.
 *
 * Inputs (both optional, assigned per page by App):
 *   slider  RecordSlider   — where each record sits while swiping
 *   anchor  HTMLElement    — [data-record-anchor], where the isolated record parks
 */
export default class RecordScene {
	constructor(records) {
		this.fov = 30
		this.arcAngle = 0.16
		this.transition = null
		this.scene = new THREE.Scene()
		this.camera = new THREE.PerspectiveCamera(this.fov, 1, 100, 10000)
		this.background = new Background()
		this.records = records.map(data => new Record(data))
		this.key = new THREE.PointLight(0xffffff, 2, 0, 0) // placed relative to the viewport in resize()
		this.scene.add(this.background, this.key, ...this.records)

		this.slider = null
		this.anchor = null
		this.activeSlug = null
		this.jumped = new Set() // records moved without animating this frame; the motion blur skips them (see Three)
		this.width = 0
		this.height = 0
		this.top = 0 // how far the canvas starts above the viewport (WebGLContext.bleed)
	}

	/** First load: records ease up into place, staggered from the centre of the slider outwards. */
	intro() {
		const order = [...this.records].sort((a, b) => Math.abs(a.position.x) - Math.abs(b.position.x))
		order.forEach(record => (record.intro = 0))
		return gsap.to(order, { intro: 1, duration: 1.4, ease: 'power3.out', stagger: 0.07 }).then()
	}

	/** Slug of the record whose page is at `url`, or null. */
	slugFor(url) {
		const pathname = new URL(url, location.href).pathname
		return this.records.find(record => new URL(record.url, location.href).pathname === pathname)?.slug ?? null
	}

	/** Every record's sequence on one timeline; reversed to come back. */
	isolate(slug, immediate = false) {
		if (slug === this.activeSlug && this.transition?.progress() === 1) return // already there (after a swap)
		this.activeSlug = slug
		this.transition?.kill()
		this.transition = gsap.timeline({ paused: true })
		this.records.forEach(record => {
			record.isActive = record.slug === slug
			this.transition.add(record.sequence(), 0)
		})
		immediate ? this.#jumpTo(1) : this.transition.play()
	}

	restore(immediate = false) {
		if (!this.transition) return
		immediate ? this.#jumpTo(0) : this.transition.reverse()
	}

	/** Put the transition at either end without animating. */
	#jumpTo(progress) {
		this.transition.progress(progress)
		this.records.forEach(record => this.jumped.add(record))
	}

	/** Pointer over / off a record in the slider; `look` is where over the sleeve it is (-1..1, y up). */
	hover(slug, on, look) {
		this.records.find(record => record.slug === slug)?.follow(on, look)
	}

	/**
	 * Pointer down / up on a record: a quick dip in, a softer release. Queued on one
	 * timeline per record so a quick tap still shows the whole dip before it springs back.
	 */
	press(slug, on) {
		const record = this.records.find(record => record.slug === slug)
		if (!record) return
		if (on) {
			record.pressTimeline?.kill()
			record.pressTimeline = gsap.timeline().to(record, { press: 1, duration: 0.12, ease: 'power2.out' })
		} else {
			;(record.pressTimeline ?? gsap.timeline()).to(record, { press: 0, duration: 0.5, ease: 'back.out(2)' })
		}
	}

	/** Clear any hover/press (the pointer is lost when the page swaps). */
	release() {
		this.records.forEach(record => {
			record.pressTimeline?.kill()
			record.follow(false)
			gsap.to(record, { press: 0, duration: 0.4, ease: 'power3.out' })
		})
	}

	/** Resolves once the current isolate/restore has finished (immediately if nothing is running). */
	whenSettled() {
		const transition = this.transition
		// (isActive() is false for a timeline that's been play()ed but not yet rendered a frame)
		const atEnd = transition && transition.progress() === (transition.reversed() ? 0 : 1)
		if (!transition || atEnd) return Promise.resolve()
		// then() resolves at either end; the timeout covers it being killed by a later navigation
		const timeout = new Promise(resolve => setTimeout(resolve, (transition.duration() + 0.5) * 1000))
		return Promise.race([transition.then(), timeout])
	}

	swap(to) {
		const outgoing = this.records.find(record => record.slug === this.activeSlug)
		const incoming = this.records.find(record => record.slug === to)
		if (!outgoing || !incoming || outgoing === incoming) return Promise.resolve()

		this.transition?.kill()
		this.activeSlug = to
		// incoming waits where the outgoing is - isolated at the anchor - a quarter turn away and
		// hidden, so its rim doesn't show through the outgoing disc
		Object.assign(incoming, { tuck: 1, flip: 1, travel: 1, exit: 0, swap: -0.5, isActive: true, visible: false })
		this.jumped.add(incoming)

		return new Promise(resolve => {
			const done = () => {
				this.transition = null // so isolate() doesn't mistake the finished swap for an existing isolation
				this.records.forEach(record =>
					Object.assign(record, { tuck: 0, flip: 0, travel: 0, exit: 0, swap: 0, visible: true })
				)
				this.isolate(to, true) // marks every record as jumped: the outgoing snaps off screen here
				resolve()
			}
			this.transition = gsap
				.timeline({ onComplete: done, onInterrupt: done })
				.to(outgoing, { swap: 0.5, duration: 0.6, ease: 'back.in(1.5)' })
				// hand over at the edge-on moment: the two discs would otherwise fight over the same space
				.add(() => {
					outgoing.visible = false
					incoming.visible = true
				})
				.to(incoming, { swap: 0, duration: 0.6, ease: 'back.out(1.5)' })
		})
	}

	update(delta) {
		this.slider?.update()
		const anchor = this.#poseFromRect(this.anchor?.getBoundingClientRect())
		const exitX = this.width / 2 // half-width: records slide out sideways past this
		this.records.forEach(record => record.update(delta, this.#sliderPose(record), anchor, exitX))
	}

	/** Canvas size in CSS px, and how far its top sits above the viewport's. */
	resize(width, height, top = 0) {
		this.width = width
		this.height = height
		this.top = top
		this.camera.aspect = width / height
		this.camera.position.z = height / 2 / Math.tan(THREE.MathUtils.degToRad(this.fov / 2))
		this.camera.updateProjectionMatrix()
		this.key.position.set(-0.35 * width, 0.45 * height, 0.5 * this.camera.position.z) // up and to the left
		this.background.resize(this.camera)
	}

	#sliderPose(record) {
		const slider = this.slider
		if (!slider) return null
		const offset = slider.offsetOf(record.slug) // in slide widths, 0 = centred
		const angle = offset * this.arcAngle
		const radius = slider.pitch / Math.sin(this.arcAngle)

		return {
			x: offset * slider.pitch,
			y: this.height / 2 - (slider.centerY + this.top) - radius * (1 - Math.cos(angle)),
			rotation: -angle,
			size: slider.slideSize,
			peek: THREE.MathUtils.clamp(1 - Math.abs(offset), 0, 1),
		}
	}

	#poseFromRect(rect) {
		if (!rect) return null
		return {
			x: rect.left + rect.width / 2 - this.width / 2,
			y: this.height / 2 - (rect.top + this.top + rect.height / 2),
			size: rect.width,
		}
	}
}
